import { AvailableLanguages } from '@repo/toolkit/language-types'
import { t } from 'i18next'
import { z } from 'zod'

type OptionsType = {
  minimumLength?: number
  maximumLength?: number
  errorMessage?: string
  isEmail?: boolean
  language?: AvailableLanguages
}

export const createStringSchema = <T extends boolean | undefined = true>(
  fieldName: string,
  options?: OptionsType &
    ({ optional?: boolean; nullish?: undefined } | { nullish?: boolean; optional?: undefined })
): T extends true
  ? z.ZodString
  : z.ZodOptional<z.ZodString> | z.ZodOptional<z.ZodNullable<z.ZodString>> => {
  const isOptional = options?.optional ?? false

  const lng = options?.language

  let schema = z
    .string({
      required_error: options?.errorMessage ?? t('errors.string.required', { fieldName, lng }),
      invalid_type_error: options?.errorMessage ?? t('errors.string.required', { fieldName, lng })
    })
    .trim()

  if (options?.isEmail)
    schema = schema.email({
      message: options?.errorMessage ?? t('errors.string.email', { fieldName, lng })
    })

  if (isOptional) {
    return schema.optional() as T extends true ? z.ZodString : z.ZodOptional<z.ZodString>
  }

  if (options?.nullish)
    return schema.nullish() as T extends true
      ? z.ZodString
      : z.ZodOptional<z.ZodNullable<z.ZodString>>

  schema = schema.min(options?.minimumLength ?? 1, {
    message: options?.errorMessage ?? t('errors.string.required', { fieldName, lng })
  })
  if (options?.maximumLength)
    schema = schema.max(options?.maximumLength, {
      message: options?.errorMessage ?? t('errors.string.max', { fieldName, lng })
    })
  return schema as T extends true ? z.ZodString : z.ZodOptional<z.ZodString>
}

type StringArraySchemaOptions = {
  minimumNumberOfItems?: number
  errorMessage?: string
  isEmail?: boolean
}

export const createStringArraySchema = (fieldName: string, options?: StringArraySchemaOptions) => {
  const baseSchema = createStringSchema(fieldName, {
    isEmail: options?.isEmail
  })

  let arraySchema: z.ZodArray<z.ZodString> = z.array(baseSchema, {
    required_error: options?.errorMessage ?? t('errors.string.list.required', { fieldName })
  })

  if (options?.minimumNumberOfItems)
    arraySchema = arraySchema.min(options.minimumNumberOfItems, {
      message:
        options?.errorMessage ??
        t('errors.string.min.list', {
          fieldName,
          minimumNumberOfItems: options.minimumNumberOfItems
        })
    })

  return arraySchema
}
